<template>
    <gi-form
        v-if="form"
        ref="form"
        :form="form"
        :on-submit-fn="onSubmitFn"
        :history="history"
        :hide-actions="hideActions"
        @success="onSuccess"
        @error="onError"
        @form-error="onFormError"
        @load-history="onLoadHistory"
        @start-history-autosave="startHistoryAutosave"
        @stop-history-autosave="stopHistoryAutosave"
    >
        <template v-if="!hideHeader" #header>
            <app-page-header class="mb-4" />
        </template>
        <template #fields="{ loading }">
            <field-user-status
                v-if="isEditing"
                ref="status"
                v-model="form.status"
                :disabled="loading"
                :exclude="[UserStatusEnum.UNVERIFIED]"
            />
            <field-string ref="gi_ref" v-model="form.gi_ref" :disabled="loading" :label="$t('gi_ref')" />
            <field-string ref="title" v-model="form.title" :disabled="loading" :label="$t('title')" />
            <field-string ref="name" v-model="form.name" :disabled="loading" required :label="$t('full_name')" />
            <field-email
                ref="email"
                v-model="form.email"
                :disabled="loading"
                :verify-user="isEditing && original && original.can_login === form.can_login ? form : null"
                :disable-verification="!form.can_login"
                required
            />
            <field-dob ref="dob" v-model="form.dob" :disabled="loading" />
            <field-string ref="job_title" v-model="form.job_title" :disabled="loading" :label="$t('job_title')" />
            <field-ni-number ref="national_insurance" v-model="form.national_insurance" :disabled="loading" />
            <field-description ref="bio" v-model="form.bio" :disabled="loading" :label="$t('bio')" />
            <field-fca-number ref="fca_number" v-model="form.fca_number" :disabled="loading" />
            <field-nationality ref="nationality" v-model="form.nationality" :disabled="loading" />
            <field-domiciled ref="domiciled" v-model="form.domiciled" :disabled="loading" />
            <field-tax-residency ref="tax_residency" v-model="form.tax_residency" :disabled="loading" />
            <field-user-tax-certificate-destination
                ref="tax_cert_destination"
                v-model="form.tax_cert_location"
                :disabled="loading"
            />
            <field-toggle
                ref="us_person"
                v-model="form.us_person"
                :disabled="loading"
                :label="$t('is_user_usa_person')"
            >
                <template #info>
                    <p class="mb-0">A US citizen is an individual to whom one or more of the following applies:</p>
                    <ul>
                        <li>Dual citizens of the US and another country</li>
                        <li>US citizen even if residing outside the United States</li>
                        <li>US passport holder</li>
                        <li>Born in the US, unless citizenship has been renounced</li>
                        <li>Lawful permanent resident of the US</li>
                        <li>A ‘substantially present’ person as declared by the US tax regulator</li>
                    </ul>
                </template>
            </field-toggle>

            <!-- eslint-disable max-len -->
            <field-toggle
                ref="politically_exposed"
                v-model="form.politically_exposed"
                :disabled="loading"
                :label="$t('is_user_politically_exposed')"
                :info="$t('if_the_applicant_or_a_close_associate_or_family_member_is_a_pep')"
            />
            <!-- eslint-enable max-len -->
            <field-toggle
                ref="can_login"
                v-model="form.can_login"
                :disabled="loading"
                :label="$t('can_login')"
                :info="$t('if_this_is_left_unticked_the_user_will_be_created_without_sending_any_emails')"
            />
            <field-phone-numbers
                ref="phone_numbers"
                v-model="form.phone_numbers"
                :disabled="loading"
                :label="$t('phone_numbers')"
                required
            />

            <field-user-roles
                ref="roles"
                v-model="form.roles"
                :disabled="loading"
                required
                @load="showHomeOrg = true"
            />
            <field-organisation
                v-if="editing && showHomeOrg"
                ref="organisation.id"
                v-model="organisation_id"
                :disabled="loading"
                :type="organisation_type"
            />
        </template>
        <template #prepend-actions>
            <app-button-cancel v-if="prependCancelButton" @click="onCancel" />
            <app-button
                :disabled="setupMfaDisabled"
                :text="$t('setup_mfa')"
                :icon="getIcon('shield', 'fad')"
                :tooltip="setupMfaDisabled ? $t('multi_factor_authentication_cant') : null"
                class="mr-4"
                secondary
                @click="form.$setupMfa()"
            />
        </template>
    </gi-form>
</template>

<script>
import form from '@/mixins/form';
import history from '@/mixins/history';
import { User } from '@/models';
import UserStatusEnum from '@/enums/user/status';

export default {
    name: 'CreateUser',
    components: {
        GiForm: () =>
            import(
                /* webpackChunkName: "forms" */
                '@/components/forms/Form'
            ),
        FieldDob: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/Dob'
            ),
        FieldEmail: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/Email'
            ),
        FieldNiNumber: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/NiNumber'
            ),
        FieldPhoneNumbers: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/PhoneNumbers'
            ),
        FieldFcaNumber: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/FcaNumber'
            ),
        FieldUserStatus: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/UserStatus'
            ),
        FieldNationality: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/Nationality'
            ),
        FieldDomiciled: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/Domiciled'
            ),
        FieldUserTaxCertificateDestination: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/UserTaxCertificateDestination'
            ),
        FieldTaxResidency: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/TaxResidency'
            ),
        FieldUserRoles: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/UserRoles'
            ),
        FieldOrganisation: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/Organisation'
            )
    },
    mixins: [form, history],
    data: () => ({
        id: 'CreateUser',
        model: User,
        UserStatusEnum,
        showHomeOrg: false
    }),
    computed: {
        setupMfaDisabled() {
            return (
                !this.form.email ||
                !this.form.email_verified ||
                [null, 'SUSPENDED', 'INACTIVE'].includes(this.form.status)
            );
        },
        organisation_id: {
            get() {
                if (!this.form.organisation || !this.form.organisation.id) {
                    return null;
                }

                return this.form.organisation.id;
            },
            set(v) {
                this.form.organisation_id = v;
            }
        },
        organisation_type: {
            get() {
                if (!this.form.organisation || !this.form.organisation.type) {
                    return null;
                }

                return this.form.organisation.type;
            },
            set(v) {
                this.form.organisation_type = v;
            }
        }
    },
    methods: {
        async getSubmissionDialog() {
            let hasAdviserRole = false;

            if (this.form && this.form.roles && this.form.roles.length) {
                hasAdviserRole = this.form.roles.some(role => role.roles.includes('ADVISER'));
            }

            if (hasAdviserRole && !this.form.fca_number) {
                return {
                    title: this.$t('fca_number_missing'),
                    text: this.$t('financial_services_register_reference_numbers_are_required'),
                    continueButtonText: this.$t('add') + ' ' + this.$t('fca_number'),
                    cancelButtonText: this.$t('continue'),
                    onCancelFn: () => this.submit({ skipDialog: true })
                };
            }
        }
    }
};
</script>
